import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ArrowBack from '@mui/icons-material/ArrowBack';
import CheckCircle from '@mui/icons-material/CheckCircle';
import FlashOn from '@mui/icons-material/FlashOn';
import Undo from '@mui/icons-material/Undo';
import { useNavigate } from 'react-router-dom';

import { getMeCached } from '../authCache';
import StudentCard from './StudentCard';
import { hasCriterionAwardToday } from './criterionAwardStatus';
import CriteriaManagerDialog from './CriteriaManagerDialog';
import { flowerForCriterion } from './criterionFlower';
import PointAwardFloat from './PointAwardFloat';
import usePointAwardAnimation from './usePointAwardAnimation';
import {
  awardStarBatch,
  createCriteria,
  deleteCriteria,
  fetchBoard,
  fetchClasses,
  fetchCriteria,
  undoLastBatchAction,
  updateCriteria,
} from './api/starGardenApi';

const toProgressMap = (students) => {
  const map = {};
  (students || []).forEach((s) => {
    map[String(s.student_id)] = {
      stage: s.stage ?? 0,
      stageStars: s.stage_stars ?? 0,
      badgesEarned: s.badges_earned ?? 0,
      lifetimeStars: s.lifetime_stars ?? 0,
      criteriaCounts: s.criteria_counts ?? {},
      todayUsedCriteriaIds: s.today_used_criteria_ids ?? [],
    };
  });
  return map;
};

const toStudentList = (students) =>
  (students || []).map((s) => ({
    id: String(s.student_id),
    firstName: s.first_name,
    lastName: s.last_name,
    classId: s.class_id,
  }));

const normalizeProgress = (s) => ({
  stage: s.stage ?? 0,
  stageStars: s.stage_stars ?? 0,
  badgesEarned: s.badges_earned ?? 0,
  lifetimeStars: s.lifetime_stars ?? 0,
  criteriaCounts: s.criteria_counts ?? {},
  todayUsedCriteriaIds: s.today_used_criteria_ids ?? [],
});

const StarGardenBatchPage = () => {
  const navigate = useNavigate();

  const [schoolId, setSchoolId] = useState(null);
  const [classes, setClasses] = useState([]);
  const [flowerCriteria, setFlowerCriteria] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);

  const [criteria, setCriteria] = useState([]);
  const [students, setStudents] = useState([]);
  const [progressMap, setProgressMap] = useState({});

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedCriteriaId, setSelectedCriteriaId] = useState(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const { awardAnimation, showPointAwards, clearPointAwards } = usePointAwardAnimation();

  const [applying, setApplying] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [lastBatch, setLastBatch] = useState(null);

  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });
  const [criteriaManagerOpen, setCriteriaManagerOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await getMeCached();
        if (cancelled) return;
        const sid = me?.data?.admin_user?.school_id;
        if (!sid) {
          setError('Okul bilgisi bulunamadı.');
          setLoading(false);
          return;
        }
        setSchoolId(sid);
      } catch (_) {
        if (!cancelled) {
          setError('Oturum bilgisi alınamadı.');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!schoolId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const list = await fetchClasses(schoolId);
        if (cancelled) return;
        const sorted = (list || []).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr-TR'));
        setClasses(sorted);
        if (sorted.length === 1) setSelectedClass(sorted[0]);
        if (sorted.length > 1) setLoading(false);
        if (sorted.length === 0) {
          setError('Bu okula ait sınıf bulunamadı.');
          setLoading(false);
        }
      } catch (_) {
        if (!cancelled) {
          setError('Sınıflar yüklenemedi.');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [schoolId]);

  const loadBoard = useCallback(async ({ silent = false } = {}) => {
    if (!schoolId || !selectedClass) return;
    if (!silent) setLoading(true);
    try {
      const board = await fetchBoard(schoolId, selectedClass.id);
      setCriteria(board.criteria || []);
      setFlowerCriteria(board.flower_criteria || board.criteria || []);
      setStudents(toStudentList(board.students));
      setProgressMap(toProgressMap(board.students));
      setSelectedStudentIds([]);
      setSelectedCriteriaId(null);
      setLastBatch(null);
      setError(null);
    } catch (e) {
      setError('Yıldız Bahçem yüklenemedi: ' + (e.message || ''));
    } finally {
      setLoading(false);
    }
  }, [schoolId, selectedClass]);

  useEffect(() => {
    if (selectedClass) loadBoard();
  }, [selectedClass, loadBoard]);

  const criteriaById = useMemo(() => {
    const map = new Map();
    criteria.forEach((c) => map.set(Number(c.id), c));
    return map;
  }, [criteria]);

  const selectedCriteria = selectedCriteriaId ? criteriaById.get(Number(selectedCriteriaId)) : null;
  const eligibleStudents = useMemo(() => students.filter((student) => !hasCriterionAwardToday(progressMap[student.id], selectedCriteriaId)), [students, progressMap, selectedCriteriaId]);
  const allStudentsSelected = eligibleStudents.length > 0 && eligibleStudents.every((student) => selectedStudentIds.includes(student.id));

  useEffect(() => {
    const eligibleIds = new Set(eligibleStudents.map((student) => student.id));
    setSelectedStudentIds((current) => current.every((id) => eligibleIds.has(id)) ? current : current.filter((id) => eligibleIds.has(id)));
  }, [eligibleStudents]);

  const onCriteriaChange = (criteriaId) => {
    if (criteriaId === selectedCriteriaId) return;
    if (selectedStudentIds.length > 0) {
      setSelectedStudentIds([]);
      setSnack({ open: true, severity: 'info', message: 'Kriter değiştiği için seçimler temizlendi.' });
    }
    setSelectedCriteriaId(criteriaId);
  };

  const toggleStudent = (studentId) => {
    if (!selectedCriteriaId || applying || reverting || hasCriterionAwardToday(progressMap[studentId], selectedCriteriaId)) return;
    setSelectedStudentIds((prev) =>
      prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]
    );
  };

  const selectAllStudents = () => {
    if (!selectedCriteriaId || applying || reverting || students.length === 0) return;
    setSelectedStudentIds(eligibleStudents.map((student) => student.id));
  };

  const clearSelectedStudents = () => {
    if (selectedStudentIds.length === 0) return;
    setSelectedStudentIds([]);
  };

  const applyBatch = async () => {
    if (!selectedCriteriaId || selectedStudentIds.length === 0 || applying || reverting) return;
    setApplying(true);
    try {
      const result = await awardStarBatch(schoolId, {
        classId: selectedClass.id,
        criteriaId: Number(selectedCriteriaId),
        studentIds: selectedStudentIds.map(Number),
      });

      const updatedStudents = result.updated_students || result.students || [];
      const failedStudents = result.failed_students || [];

      if (updatedStudents.length > 0) {
        setProgressMap((prev) => {
          const next = { ...prev };
          updatedStudents.forEach((student) => {
            const sid = String(student.student_id);
            const previous = prev[sid]?.criteriaCounts || {};
            next[sid] = {
              ...normalizeProgress(student),
              criteriaCounts: {
                ...previous,
                [String(selectedCriteriaId)]: Number(previous[String(selectedCriteriaId)] || 0) + 1,
              },
            };
          });
          return next;
        });

        const changedIds = updatedStudents.map((s) => String(s.student_id));
        showPointAwards(changedIds);
      }

      const batchId = result.batch_id ?? result.action_batch_id ?? null;
      setLastBatch({
        batchId,
        criteriaId: Number(selectedCriteriaId),
        studentIds: selectedStudentIds,
      });

      const okCount = updatedStudents.length;
      const failCount = failedStudents.length;
      const msg = failCount > 0
        ? `${okCount} öğrenciye uygulandı, ${failCount} öğrenciye uygulanamadı.`
        : `${okCount} öğrenciye yıldız verildi!`;
      setSnack({ open: true, severity: failCount > 0 ? 'warning' : 'success', message: msg });
      setSelectedStudentIds([]);
    } catch (e) {
      setSnack({ open: true, severity: 'error', message: 'Toplu uygulama başarısız: ' + (e.message || '') });
    } finally {
      setApplying(false);
    }
  };

  const undoLastBatch = async () => {
    if (!lastBatch || reverting || applying) return;
    setReverting(true);
    try {
      await undoLastBatchAction(schoolId, {
        classId: selectedClass.id,
        batchId: lastBatch.batchId,
      });
      const board = await fetchBoard(schoolId, selectedClass.id);
      setProgressMap(toProgressMap(board.students));
      clearPointAwards();
      setLastBatch(null);
      setSnack({ open: true, severity: 'success', message: 'Son toplu işlem geri alındı.' });
    } catch (e) {
      setSnack({ open: true, severity: 'error', message: 'Geri alma başarısız: ' + (e.message || '') });
    } finally {
      setReverting(false);
    }
  };

  if (!loading && schoolId && classes.length > 1 && !selectedClass) {
    return (
      <Box
        sx={{
          height: '100vh',
          width: '100vw',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
          bgcolor: '#e8f5e9',
          userSelect: 'none',
          p: 4,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#2e7d32', mb: 1 }}>
          ⚡ Yıldız Bahçem Hızlı
        </Typography>
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>
          Sınıfınızı seçin
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', maxWidth: 600 }}>
          {classes.map((cls) => (
            <Button
              key={cls.id}
              variant="contained"
              onClick={() => setSelectedClass(cls)}
              sx={{
                px: 4,
                py: 2,
                fontSize: 18,
                fontWeight: 700,
                borderRadius: 3,
                textTransform: 'none',
                background: 'linear-gradient(135deg, #66bb6a, #43a047)',
                color: '#fff',
                minWidth: 120,
                '&:hover': {
                  background: 'linear-gradient(135deg, #4caf50, #388e3c)',
                },
              }}
            >
              {cls.name}
            </Button>
          ))}
        </Box>
        <Button
          variant="outlined"
          onClick={() => navigate('/admin/stargarden')}
          sx={{
            mt: 1,
            textTransform: 'none',
            borderRadius: 3,
            fontWeight: 700,
            borderColor: '#2e7d32',
            color: '#2e7d32',
            '&:hover': { bgcolor: '#e8f5e9', borderColor: '#1b5e20' },
          }}
        >
          🌼 Yıldız Bahçem
        </Button>
        <Button
          variant="text"
          onClick={() => navigate('/admin')}
          sx={{ mt: 2, textTransform: 'none', color: 'text.secondary' }}
        >
          ← Panele dön
        </Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ height: '100vh', width: '100vw', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2, bgcolor: '#e8f5e9' }}>
        <CircularProgress size={52} sx={{ color: '#43a047' }} />
        <Typography sx={{ fontWeight: 700, color: '#2e7d32' }}>Hızlı mod hazırlanıyor...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ height: '100vh', width: '100vw', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#e8f5e9', p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: 'linear-gradient(180deg, #e8f5e9 0%, #c8e6c9 100%)', background: 'linear-gradient(180deg, #e8f5e9 0%, #c8e6c9 100%)', pb: 12 }}>
      <Box sx={{ position: 'sticky', top: 0, zIndex: 6, backdropFilter: 'blur(8px)', bgcolor: 'rgba(232,245,233,0.9)', borderBottom: '1px solid rgba(46,125,50,0.15)' }}>
        <Box sx={{ px: { xs: 1.5, md: 3 }, py: 1.25, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title="Yıldız Bahçem">
            <IconButton onClick={() => navigate('/admin/stargarden')}>
              <ArrowBack />
            </IconButton>
          </Tooltip>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, color: '#2e7d32', fontSize: 'clamp(18px, 2.4vw, 30px)' }}>
              ⚡ Yıldız Bahçem Hızlı
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: 'clamp(12px, 1.2vw, 14px)' }}>
              Önce kriteri seçin, sonra öğrenci kartlarına dokunun.
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {!!selectedClass && (
              <Chip label={`📚 ${selectedClass.name}`} sx={{ fontWeight: 700, bgcolor: 'rgba(46,125,50,0.12)' }} />
            )}
            {classes.length > 1 && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setSelectedClass(null);
                  setStudents([]);
                  setCriteria([]);
                  setSelectedStudentIds([]);
                  setSelectedCriteriaId(null);
                }}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: 'clamp(11px, 1vw, 14px)',
                  borderColor: '#ff9800',
                  color: '#e65100',
                  '&:hover': { bgcolor: '#fff3e0', borderColor: '#e65100' },
                }}
              >
                Sınıf Değiştir
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={() => setCriteriaManagerOpen(true)}
              sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700 }}
            >
              Kriter Güncelle
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/admin/stargarden')}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 700,
                fontSize: 'clamp(11px, 1vw, 14px)',
                borderColor: '#2e7d32',
                color: '#2e7d32',
                '&:hover': { bgcolor: '#e8f5e9', borderColor: '#1b5e20' },
              }}
            >
              🌼 Yıldız Bahçem
            </Button>
          </Box>
        </Box>

        <Box sx={{ px: { xs: 1.5, md: 3 }, pb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {criteria.map((criterion) => {
            const selected = Number(selectedCriteriaId) === Number(criterion.id);
            return (
              <Chip
                key={criterion.id}
                label={`${flowerForCriterion(flowerCriteria, criterion.id)} ${criterion.name}`}
                color={selected ? 'success' : 'default'}
                variant={selected ? 'filled' : 'outlined'}
                onClick={() => onCriteriaChange(Number(criterion.id))}
                sx={{ height: 38, fontSize: 14, fontWeight: selected ? 700 : 500 }}
              />
            );
          })}
        </Box>
      </Box>

      <Box sx={{ px: { xs: 1, md: 2 }, pt: 1.5 }}>
        {!selectedCriteria && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Hızlı seçim için önce bir kriter seçin.
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
            gap: { xs: 1.5, md: 2.25 },
            alignItems: 'stretch',
          }}
        >
          {students.map((student) => {
            const selected = selectedStudentIds.includes(student.id);
            const awardedToday = hasCriterionAwardToday(progressMap[student.id], selectedCriteriaId);
            return (
              <Box key={student.id} sx={{ position: 'relative' }}>
                <StudentCard
                  student={student}
                  progress={progressMap[student.id] || { stage: 0, stageStars: 0, badgesEarned: 0, lifetimeStars: 0, todayUsedCriteriaIds: [] }}
                  flowerCriteria={flowerCriteria}
                  compact
                  selected={selected}
                  selectable={!!selectedCriteria && !awardedToday}
                  awardedToday={awardedToday}
                  onClick={() => toggleStudent(student.id)}
                  animateStage={awardAnimation.studentIds.includes(student.id)}
                />
                {awardAnimation.studentIds.includes(student.id) && (
                  <PointAwardFloat key={`${student.id}-${awardAnimation.sequence}`} />
                )}
                {selected && (
                  <Box sx={{ position: 'absolute', top: 8, right: 8, width: 28, height: 28, borderRadius: '50%', bgcolor: '#2e7d32', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 3 }}>
                    <CheckCircle fontSize="small" />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 7, p: { xs: 1, md: 1.5 }, bgcolor: 'rgba(255,255,255,0.96)', borderTop: '1px solid rgba(0,0,0,0.08)' }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 700, color: '#2e7d32', mr: 'auto' }}>
            {selectedCriteria ? `${selectedStudentIds.length} öğrenci seçildi` : 'Kriter seçilmedi'}
          </Typography>
          <Chip
            icon={<FlashOn />}
            label={selectedCriteria ? `Kriter: ${selectedCriteria.name}` : 'Önce kriter seçin'}
            color={selectedCriteria ? 'success' : 'default'}
            variant={selectedCriteria ? 'filled' : 'outlined'}
          />
          <Button
            variant="outlined"
            disabled={!selectedCriteria || allStudentsSelected || applying || reverting || eligibleStudents.length === 0}
            onClick={selectAllStudents}
            sx={{ textTransform: 'none', borderRadius: 3 }}
          >
            Tümünü Seç
          </Button>
          <Button
            variant="outlined"
            disabled={selectedStudentIds.length === 0 || applying || reverting}
            onClick={clearSelectedStudents}
            sx={{ textTransform: 'none', borderRadius: 3 }}
          >
            Seçimleri Kaldır
          </Button>
          <Button
            variant="outlined"
            startIcon={<Undo />}
            disabled={!lastBatch || applying || reverting}
            onClick={undoLastBatch}
            sx={{ textTransform: 'none', borderRadius: 3 }}
          >
            Son toplu işlemi geri al
          </Button>
          <Button
            variant="contained"
            disabled={!selectedCriteria || selectedStudentIds.length === 0 || applying || reverting}
            onClick={applyBatch}
            sx={{ textTransform: 'none', borderRadius: 3, px: 2.5, fontWeight: 700 }}
          >
            {applying ? 'Uygulanıyor...' : `${selectedStudentIds.length} öğrenciye uygula`}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={snack.severity === 'success' ? 5000 : 3500}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
          action={
            snack.severity === 'success' && lastBatch ? (
              <Button color="inherit" size="small" onClick={undoLastBatch} disabled={reverting}>
                GERİ AL
              </Button>
            ) : null
          }
        >
          {snack.message}
        </Alert>
      </Snackbar>
      <CriteriaManagerDialog
        open={criteriaManagerOpen}
        onClose={() => setCriteriaManagerOpen(false)}
        scopeLabel={selectedClass?.name || 'Sınıf'}
        loadCriteria={() => fetchCriteria(schoolId, selectedClass?.id)}
        createCriterion={({ name, icon, sort_order: sortOrder }) => createCriteria(schoolId, { classId: selectedClass.id, name, icon, sortOrder })}
        updateCriterion={(id, { name, icon, sort_order: sortOrder }) => updateCriteria(schoolId, id, { name, icon, sortOrder })}
        deleteCriterion={(id) => deleteCriteria(schoolId, id)}
        onChanged={() => loadBoard({ silent: true })}
      />
    </Box>
  );
};

export default StarGardenBatchPage;
